import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { activityLogsAPI, usersAPI } from '../services/api';
import { ActivityLog, User } from '../types';

interface ActivityFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  action?: string | null;
  entityType?: string | null;
  userId?: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  login: 'LOGIN',
  logout: 'LOGOUT',
  create: 'Kreiranje',
  update: 'Ažuriranje',
  delete: 'Brisanje',
  view: 'Pregled',
  assign: 'Dodela',
  return: 'Vraćanje',
};

const ActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState<ActivityFilters>({});

  useEffect(() => {
    void loadUsers();
  }, []);

  useEffect(() => {
    void loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const loadUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading users for filters', err);
    }
  };

  const buildQueryParams = () => {
    const params: any = {
      page: pagination.page,
      limit: pagination.limit,
    };
    if (filters.action) params.action = filters.action;
    if (filters.entityType) params.entityType = filters.entityType;
    if (filters.userId) params.userId = filters.userId;
    if (filters.startDate)
      params.startDate = filters.startDate.toISOString().split('T')[0];
    if (filters.endDate)
      params.endDate = filters.endDate.toISOString().split('T')[0];
    return params;
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      const data = await activityLogsAPI.getAll(params);
      setLogs(data.activities);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination.total,
        pages: data.pagination.pages,
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading activity logs', err);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
    void loadLogs();
  };

  const handleExport = async () => {
    try {
      const params = buildQueryParams();
      const blob = await activityLogsAPI.exportCSV(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-logs-${new Date()
        .toISOString()
        .split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error exporting activity logs', err);
    }
  };

  const handleDeleteAll = () => {
    confirmDialog({
      message: 'Da li ste sigurni da želite da obrišete sve logove?',
      header: 'Brisanje logova',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Da, obriši sve',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await activityLogsAPI.deleteAll();
          setPagination((prev) => ({ ...prev, page: 1 }));
          void loadLogs();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error deleting all logs', err);
        }
      },
    });
  };

  const dateBody = (row: ActivityLog) =>
    new Date(row.timestamp).toLocaleString('sr-RS');

  const actionBody = (row: ActivityLog) => {
    const label = ACTION_LABELS[row.action] || row.action;
    let severity: 'success' | 'info' | 'warning' | 'danger' = 'info';
    if (row.action === 'login') severity = 'success';
    else if (row.action === 'logout') severity = 'warning';
    else if (row.action === 'delete') severity = 'danger';
    return <Tag value={label} severity={severity} />;
  };

  const userBody = (row: ActivityLog) =>
    row.userName ||
    (typeof row.userId === 'object'
      ? `${row.userId.firstName} ${row.userId.lastName}`
      : 'N/A');

  const ipBody = (row: ActivityLog) => row.ipAddress || 'N/A';

  return (
    <div className="p-4">
      <h2>Activity Logs</h2>

      <div className="grid mb-3">
        <div className="col-12 md:col-4">
          <span className="block mb-2 text-sm text-gray-600">Datum</span>
          <Calendar
            value={[filters.startDate || null, filters.endDate || null]}
            onChange={(e) => {
              const range = (e.value || []) as (Date | null)[];
              const [start, end] = range;
              setFilters((prev) => ({
                ...prev,
                startDate: start || null,
                endDate: end || null,
              }));
            }}
            placeholder="Izaberite period"
            dateFormat="dd.mm.yy"
            selectionMode="range"
          />
        </div>
        <div className="col-12 md:col-4">
          <span className="block mb-2 text-sm text-gray-600">Akcija</span>
          <Dropdown
            value={filters.action || null}
            options={[
              { label: 'Sve akcije', value: null },
              { label: 'LOGIN', value: 'login' },
              { label: 'LOGOUT', value: 'logout' },
              { label: 'Kreiranje', value: 'create' },
              { label: 'Ažuriranje', value: 'update' },
              { label: 'Brisanje', value: 'delete' },
            ]}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, action: e.value || null }))
            }
            placeholder="Sve akcije"
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-4">
          <span className="block mb-2 text-sm text-gray-600">Korisnik</span>
          <Dropdown
            value={filters.userId || null}
            options={[
              { label: 'Svi korisnici', value: null },
              ...users.map((u) => ({
                label: `${u.firstName} ${u.lastName}`,
                value: u._id,
              })),
            ]}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, userId: e.value || null }))
            }
            placeholder="Svi korisnici"
            className="w-full"
          />
        </div>
      </div>

      <div className="flex justify-content-between align-items-center mb-3">
        <div>
          Ukupno {pagination.total} logova
        </div>
        <div className="flex gap-2">
          <Button
            label="Očisti filtere"
            icon="pi pi-filter-slash"
            onClick={resetFilters}
            className="p-button-outlined"
          />
          <Button
            label="Eksportuj CSV"
            icon="pi pi-download"
            onClick={handleExport}
          />
          <Button
            label="Obriši sve logove"
            icon="pi pi-trash"
            severity="danger"
            onClick={handleDeleteAll}
          />
        </div>
      </div>

      <Card>
        <DataTable
          value={logs}
          loading={loading}
          paginator
          rows={pagination.limit}
          totalRecords={pagination.total}
          first={(pagination.page - 1) * pagination.limit}
          onPage={(e) =>
            setPagination((prev) => ({ ...prev, page: (e.page || 0) + 1 }))
          }
          emptyMessage="Nema logova"
          className="p-datatable-sm"
        >
          <Column
            field="timestamp"
            header="Vreme"
            body={dateBody}
            sortable
          />
          <Column field="action" header="Akcija" body={actionBody} sortable />
          <Column field="userName" header="Korisnik" body={userBody} sortable />
          <Column
            field="entityName"
            header="Entitet"
            sortable
          />
          <Column field="entityType" header="Tip entiteta" sortable />
          <Column field="ipAddress" header="IP Adresa" body={ipBody} />
        </DataTable>
      </Card>

      <ConfirmDialog />
    </div>
  );
};

export default ActivityLogs;


